/**
 * DeliveryPhotoWall — 3-column vertical photo wall with staggered-speed
 * parallax scroll. Driven by a single `requestAnimationFrame` loop that
 * mutates `transform: translate3d(0, -Ypx, 0)` on each column's track — no
 * React re-renders per frame, so it's cheap even with 60+ images.
 *
 * Seamless looping: each column renders the photo list twice; we modulo the
 * scroll offset against `track.scrollHeight / 2`.
 *
 * Respects `prefers-reduced-motion` — if reduced, the wall sits static.
 *
 * Accessibility: decorative (aria-hidden="true").
 *
 * Zero-flash boot: the last-known set of delivery photo URLs is cached in
 * localStorage. On every subsequent mount we paint from the cache on the
 * first frame, so Jake never sees a placeholder-then-swap. Fresh URLs are
 * fetched in the background and quietly replace the cache once they arrive.
 */
import { useEffect, useState, useMemo, useRef } from "react";

// iter 370jp — Bumped from v1 → v2 to evict every user's localStorage
// cache of stale tokens. Historically the backend was minting tokens
// for orphaned photo_storage references; old cached tokens won't
// resolve on the new backend either. Bumping the key forces a fresh
// fetch on next visit.
const CACHE_KEY = "login_photo_wall_urls_v2";
// px/s. Col 2 fastest so the parallax feels deliberate, not mechanical.
const COLUMN_SPEEDS = [22, 36, 28];

function readCachedPhotos() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length >= 3 && parsed.every(u => typeof u === "string")) {
      return parsed;
    }
  } catch {
    /* cache corrupt — fall through */
  }
  return null;
}

function chunk(arr, n) {
  const cols = Array.from({ length: n }, () => []);
  arr.forEach((item, i) => { cols[i % n].push(item); });
  return cols;
}

export function DeliveryPhotoWall({ apiBase }) {
  // Seed from cache on first render — painted on the same frame, no flash.
  const [photos, setPhotos] = useState(() => readCachedPhotos() || []);
  const trackRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/public/login-photos`, { credentials: "omit" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data.photos) && data.photos.length >= 3) {
          setPhotos(data.photos);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(data.photos));
          } catch {
            /* storage full / private mode — not fatal */
          }
        }
      } catch {
        /* offline / backend down — keep whatever we painted from cache */
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  const columns = useMemo(() => {
    if (!photos.length) return [[], [], []];
    // Need a dense pool per column so the loop wraps off-screen smoothly.
    let pool = photos;
    while (pool.length < 24) pool = [...pool, ...photos];
    return chunk(pool, 3);
  }, [photos]);

  // rAF-driven scroll — zero React re-renders per frame.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (reduce?.matches) return;

    let raf = 0;
    let last = performance.now();
    const offsets = [0, 0, 0];

    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000); // cap at 100ms to survive tab blur
      last = now;
      for (let i = 0; i < 3; i++) {
        const track = trackRefs.current[i];
        if (!track) continue;
        const halfH = track.scrollHeight / 2;
        if (halfH <= 0) continue;
        offsets[i] = (offsets[i] + COLUMN_SPEEDS[i] * dt) % halfH;
        track.style.transform = `translate3d(0, ${-offsets[i]}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // Pause while the tab isn't visible — keeps CPU cool.
    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else {
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [columns]);

  return (
    <div className="absolute inset-0 bg-black overflow-hidden" aria-hidden="true">
      <div className="grid grid-cols-3 gap-1.5 p-1.5 h-full">
        {columns.map((colPhotos, i) => (
          <div key={i} className="relative overflow-hidden">
            <div
              ref={el => (trackRefs.current[i] = el)}
              className="absolute inset-x-0 flex flex-col gap-1.5"
              style={{ willChange: "transform" }}
            >
              {[...colPhotos, ...colPhotos].map((url, idx) => (
                <img
                  key={`${i}-${idx}`}
                  src={url}
                  alt=""
                  draggable={false}
                  // First handful of images load eager + high-priority so the
                  // cache-hit path paints on the first frame. Rest stay lazy.
                  loading={idx < 3 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={idx < 3 ? "high" : "auto"}
                  // iter 370jp — Belt-and-suspenders: if a token still
                  // 404s for any reason (orphaned reference slipping
                  // past the backend filter, transient object-storage
                  // miss, etc.), hide the tile completely instead of
                  // rendering the browser's broken-image icon. Removing
                  // the src as well stops the browser from re-trying
                  // the broken URL on re-renders.
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.removeAttribute("src");
                  }}
                  className="w-full aspect-[4/5] object-cover rounded-md ring-1 ring-white/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.8)]"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Hard black edge → transparent over 5% of the column so the scroll
          fully disappears at the viewport edges without a soft mid-fade. */}
      <div className="absolute inset-x-0 top-0 h-[5vh] bg-gradient-to-b from-black from-0% to-transparent to-100% pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-[5vh] bg-gradient-to-t from-black from-0% to-transparent to-100% pointer-events-none" />
    </div>
  );
}
